import { BRAND } from "@/config/brand";
import { BLOG_BASE_URL, MAIN_BASE_URL, isOnBlogSubdomain } from "@/lib/blog-host";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MailingListModal } from "./MailingListModal";

// Internal links use <Link> for SPA nav on the main host; the Blog link is
// EXTERNAL (the blog subdomain) so the leaving-site modal intercepts it. When
// the SPA is served ON the blog host, internal targets are re-rendered as
// absolute main-host <a href> so the modal also catches blog → main hops.
type NavLink =
  | { label: string; to: string; external?: false }
  | { label: string; href: string; external: true };

const navLinks: readonly NavLink[] = [
  { label: "Products", to: "/products" },
  { label: "Company", to: "/company" },
  { label: "Blog", href: `${BLOG_BASE_URL}/`, external: true },
] as const;

const THEME_STORAGE_KEY = "corgi-theme";

export function EtfNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isMailingListOpen, setIsMailingListOpen] = useState(false);
  const { pathname } = useLocation();
  // When served ON the blog host, internal targets live on the main marketing
  // host — render them as absolute <a href> so the leaving-site modal sees a
  // cross-host navigation. On the main host, keep <Link> for SPA navigation.
  const onBlog = isOnBlogSubdomain();
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const contactBtnRef = useRef<HTMLButtonElement>(null);
  const contactMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        hamburgerRef.current &&
        !hamburgerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen]);

  // Contact dropdown — close on outside click + Escape.
  useEffect(() => {
    if (!isContactOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        contactBtnRef.current &&
        !contactBtnRef.current.contains(target) &&
        contactMenuRef.current &&
        !contactMenuRef.current.contains(target)
      ) {
        setIsContactOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsContactOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isContactOpen]);

  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(THEME_STORAGE_KEY) as "light" | "dark") || "dark";
    }
    return "dark";
  });

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (next === "dark") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", next);
    }
    localStorage.setItem(THEME_STORAGE_KEY, next);
  };

  return (
    <>
      <nav className="sticky top-0 z-[200] flex items-center h-14 px-10 max-nav:px-5 border-b border-[var(--etf-border)] bg-[var(--etf-black)]">
        {onBlog ? (
          <a href={`${MAIN_BASE_URL}/`} className="flex items-center mr-12 no-underline">
            <img
              src={BRAND.logos.dark}
              alt="Corgi Funds"
              className="h-7 w-auto block dark-only-logo"
              loading="eager"
            />
            <img
              src={BRAND.logos.light}
              alt="Corgi Funds"
              className="h-7 w-auto block light-only-logo"
              loading="eager"
            />
          </a>
        ) : (
          <Link to="/" className="flex items-center mr-12 no-underline">
            <img
              src={BRAND.logos.dark}
              alt="Corgi Funds"
              className="h-7 w-auto block dark-only-logo"
              loading="eager"
            />
            <img
              src={BRAND.logos.light}
              alt="Corgi Funds"
              className="h-7 w-auto block light-only-logo"
              loading="eager"
            />
          </Link>
        )}

        <ul className="flex items-center flex-1 list-none gap-0 max-nav:!hidden">
          {navLinks.map((link, i) => {
            const linkClass = cn(
              "text-sm font-[family-name:var(--font-instrument)] text-[var(--etf-white2)] no-underline tracking-[0.02em] hover:text-[var(--etf-white)] transition-colors",
              !link.external &&
                !onBlog &&
                pathname.startsWith(link.to) &&
                "text-[var(--etf-white)]",
            );
            return (
              <li key={link.label} className="flex items-center">
                {link.external ? (
                  <a href={link.href} className={linkClass}>
                    {link.label}
                  </a>
                ) : onBlog ? (
                  <a href={`${MAIN_BASE_URL}${link.to}`} className={linkClass}>
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.to} className={linkClass}>
                    {link.label}
                  </Link>
                )}
                {i < navLinks.length - 1 && (
                  <span className="mx-3 text-[10px] text-[var(--etf-white3)] opacity-50 tracking-[-3px]">
                    ——
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="bg-transparent border border-[var(--etf-border)] text-[var(--etf-white2)] w-9 h-9 flex items-center justify-center cursor-pointer mr-3 p-0 shrink-0 transition-all duration-150 hover:border-[var(--etf-white2)] hover:text-[var(--etf-white)]"
          onClick={toggleTheme}
          aria-label="Toggle light/dark mode"
        >
          {theme === "light" && (
            <svg
              aria-hidden="true"
              focusable="false"
              className="w-4 h-4 block"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="5" />
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
          )}
          {theme === "dark" && (
            <svg
              aria-hidden="true"
              focusable="false"
              className="w-4 h-4 block"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>

        <div className="relative max-nav:!hidden mr-3">
          <button
            ref={contactBtnRef}
            type="button"
            onClick={() => setIsContactOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={isContactOpen}
            className={cn(
              "px-3.5 h-9 inline-flex items-center gap-1.5 text-[16px] font-medium font-[family-name:var(--font-instrument)] tracking-[0.04em] cursor-pointer transition-colors duration-150 border bg-transparent text-[var(--etf-white)]",
              isContactOpen
                ? "border-[var(--etf-white2)]"
                : "border-[var(--etf-border)] hover:border-[var(--etf-white2)]",
            )}
          >
            Contact Us
            <svg
              aria-hidden="true"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              className={cn("transition-transform duration-150", isContactOpen && "rotate-180")}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 4.5 6 7.5 9 4.5" />
            </svg>
          </button>
          {isContactOpen && (
            <div
              ref={contactMenuRef}
              className="absolute top-[calc(100%+6px)] right-0 z-[210] min-w-[220px] bg-[var(--etf-black)] border border-[var(--etf-border)] shadow-[0_12px_28px_rgba(0,0,0,0.5)]"
            >
              <a
                href={`mailto:${BRAND.contactEmail}`}
                onClick={() => setIsContactOpen(false)}
                className="block px-4 py-3 text-sm font-[family-name:var(--font-instrument)] tracking-[0.02em] text-[var(--etf-white2)] no-underline hover:bg-[var(--etf-black2)] hover:text-[var(--etf-white)] transition-colors border-b border-[var(--etf-border2)]"
              >
                Email
                <span className="block text-[11px] text-[var(--etf-white3)] tracking-[0.02em] mt-0.5">
                  {BRAND.contactEmail}
                </span>
              </a>
              <button
                type="button"
                onClick={() => {
                  setIsContactOpen(false);
                  setIsMailingListOpen(true);
                }}
                className="block w-full text-left px-4 py-3 text-sm font-[family-name:var(--font-instrument)] tracking-[0.02em] bg-transparent border-none text-[var(--etf-white2)] cursor-pointer hover:bg-[var(--etf-black2)] hover:text-[var(--etf-white)] transition-colors"
              >
                Join Mailing List
                <span className="block text-[11px] text-[var(--etf-white3)] tracking-[0.02em] mt-0.5">
                  Subscribe to The Corgi Brief
                </span>
              </button>
            </div>
          )}
        </div>

        <button
          ref={hamburgerRef}
          type="button"
          className="hidden max-nav:!flex flex-col justify-center items-center gap-[5px] w-9 h-9 bg-transparent border-none cursor-pointer p-1 shrink-0 ml-3"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "translate-y-[6.5px] rotate-45",
            )}
          />
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "opacity-0 scale-x-0",
            )}
          />
          <span
            className={cn(
              "block w-5 h-[1.5px] bg-[var(--etf-white)] transition-all duration-[250ms] origin-center",
              isMenuOpen && "-translate-y-[6.5px] -rotate-45",
            )}
          />
        </button>
      </nav>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className="fixed top-14 left-0 right-0 bg-[var(--etf-black)] border-b border-[var(--etf-border)] z-[198] flex flex-col px-5 pt-2 pb-4 shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
        >
          {navLinks.map((link, i) => {
            const isLast = i === navLinks.length - 1;
            const mobileClass = cn(
              "font-[family-name:var(--font-instrument)] text-[15px] text-[var(--etf-white2)] no-underline py-3.5 tracking-[0.04em] transition-colors duration-150 hover:text-[var(--etf-white)]",
              !isLast && "border-b border-[var(--etf-border2)]",
            );
            const closeMenu = () => setIsMenuOpen(false);
            return link.external ? (
              <a key={link.label} href={link.href} className={mobileClass} onClick={closeMenu}>
                {link.label}
              </a>
            ) : onBlog ? (
              <a
                key={link.label}
                href={`${MAIN_BASE_URL}${link.to}`}
                className={mobileClass}
                onClick={closeMenu}
              >
                {link.label}
              </a>
            ) : (
              <Link key={link.label} to={link.to} className={mobileClass} onClick={closeMenu}>
                {link.label}
              </Link>
            );
          })}
          <a
            href={`mailto:${BRAND.contactEmail}`}
            className="font-[family-name:var(--font-instrument)] text-[15px] text-[var(--etf-white2)] no-underline py-3.5 tracking-[0.04em] transition-colors duration-150 hover:text-[var(--etf-white)] border-t border-[var(--etf-border2)]"
            onClick={() => setIsMenuOpen(false)}
          >
            Email Us
          </a>
          <button
            type="button"
            className="text-left font-[family-name:var(--font-instrument)] text-[15px] text-[var(--etf-white2)] py-3.5 tracking-[0.04em] transition-colors duration-150 hover:text-[var(--etf-white)] bg-transparent border-none border-t border-[var(--etf-border2)] cursor-pointer p-0"
            onClick={() => {
              setIsMenuOpen(false);
              setIsMailingListOpen(true);
            }}
          >
            Join Mailing List
          </button>
        </div>
      )}
      <MailingListModal open={isMailingListOpen} onClose={() => setIsMailingListOpen(false)} />
    </>
  );
}
